/**
 * Diamond Pairs — the dealer (specs/pairs-mode.md §4.1, §6).
 *
 * Phaser-free so it can be tested in node, like game/pairs/rules.ts. Everything about *what* is on
 * the board lives here; PairsScene only draws what this returns.
 */
import { PAIRS } from '@/game/config';
import type { PackWord } from '@/data/packs/types';

/** One picture/word pair: the icon id, the word in the player's language, and its topic. */
export interface PairWord {
  id: string;
  text: string;
  topic: string;
}

/** One dealt card. `pairId` is the icon id — two cards match iff their `pairId`s are equal. */
export interface DealtCard {
  pairId: string;
  kind: 'picture' | 'word';
  /** The word's surface form; carried on both cards so the picture card can speak/label on match. */
  text: string;
}

/** Pairs on the board in `round` (1-based). The last rung of the ladder repeats forever. */
export function pairsForRound(round: number): number {
  const i = Math.min(Math.max(1, Math.floor(round)), PAIRS.ladder.length) - 1;
  return PAIRS.ladder[i];
}

/** Grid for `round` as [cols, rows]. cols × rows === 2 × pairsForRound(round) — see deal.test.ts. */
export function gridFor(round: number): [number, number] {
  const i = Math.min(Math.max(1, Math.floor(round)), PAIRS.grids.length) - 1;
  return PAIRS.grids[i];
}

/**
 * Words this mode can put on a card: an icon id, a surface form short enough for the smallest card
 * face, and a topic. Length is a real constraint — the round-4 card is 169px wide with an 18px font
 * floor — and it is checked as DATA by data/pools/picturewords-cardfit.test.ts, so an overlong word
 * fails the build rather than overflowing its card at runtime.
 */
export function eligibleWords(words: readonly PackWord[]): PairWord[] {
  const out: PairWord[] = [];
  for (const w of words) {
    const id = typeof w.id === 'string' ? w.id : '';
    if (!id || typeof w.text !== 'string' || w.text.length > PAIRS.maxWordChars) continue;
    out.push({ id, text: w.text, topic: typeof w.class === 'string' ? w.class : '' });
  }
  return out;
}

/** Unbiased Fisher–Yates. Returns a new array; `rnd` is injectable so tests can pin an order. */
export function shuffle<T>(items: readonly T[], rnd: () => number = Math.random): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * The pairs for one round (§6.4). Three things are random here, and each one has a failure mode:
 *
 *  1. **Which words** — sampled from the front `workingSetSize` of the learned-first ordering, NOT
 *     taken off the top. Taking the top 12 would deal the same round 1 every time, since the
 *     ordering only changes when mastery does, which is days apart.
 *  2. **Which topics** — `topicsPerRound` picked at random from those the working set covers. One
 *     topic makes twelve pictures hard to tell apart at 105px; twelve topics read as noise.
 *  3. Nothing about position: that is `dealDeck`'s job.
 *
 * `used` holds the ids already dealt in this run, so a word is never dealt twice — until the pool
 * runs dry, at which point the caller clears it and repeats are allowed rather than the run ending.
 * Widening is graceful: too few words in the chosen topics → more topics → the whole working set →
 * the whole eligible pool.
 */
export function pickPairs(
  pool: readonly PairWord[],
  count: number,
  used: ReadonlySet<string>,
  rnd: () => number = Math.random,
): PairWord[] {
  const fresh = pool.filter((w) => !used.has(w.id));
  const available = fresh.length >= count ? fresh : [...pool];   // pool exhausted: allow repeats
  const workingSet = available.slice(0, Math.max(count * 2, PAIRS.workingSetSize));

  const topics = shuffle([...new Set(workingSet.map((w) => w.topic))], rnd);
  const chosen: PairWord[] = [];
  // The first `topicsPerRound` topics always go in; further ones only while the round is short of
  // words — so the mix is a fixed handful of topics, widened only when it has to be.
  for (let i = 0; i < topics.length; i++) {
    if (i >= PAIRS.topicsPerRound && chosen.length >= count) break;
    chosen.push(...workingSet.filter((w) => w.topic === topics[i]));
  }
  const candidates = chosen.length >= count ? chosen
    : workingSet.length >= count ? workingSet
      : available;

  return shuffle(candidates, rnd).slice(0, count);
}

/**
 * The deck for a round: one picture card and one word card per pair, then a single Fisher–Yates
 * pass over the WHOLE deck.
 *
 * The pass has to come after both halves are built. Shuffling the pairs and then laying
 * picture-then-word is the classic bug: the board looks shuffled and is solvable by stepping two
 * cells at a time, without reading anything.
 */
export function dealDeck(pairs: readonly PairWord[], rnd: () => number = Math.random): DealtCard[] {
  const deck: DealtCard[] = [];
  for (const p of pairs) {
    deck.push({ pairId: p.id, kind: 'picture', text: p.text });
    deck.push({ pairId: p.id, kind: 'word', text: p.text });
  }
  return shuffle(deck, rnd);
}
